import React, { useContext, useEffect } from 'react';
import ServerContext from '../../../contexts/serverContext';
import './category-page-view.scss';
import { SecondaryButton } from '../../../components/common';
import { FdsChevron } from '../../../components/common/chevron/fds-chevron';
import BbtCategories from '../bbt-categories/bbt-categories';
import { useSearchContent } from '../../../components/sections/search-bar/hooks/use-search-content';
import { useBbtContent } from '../browse-by-topic/hook/use-bbt-content';
import { useAppInformationContent } from '../../../components/sections/breadcrumbs/hook/use-breadcrumb-content';
import { BRAND_ATTRIBUTES } from '../../support-constants';
import { SlackMessages } from '../../../services/slack-message-service/SlackMessageService';
import { useServerData } from '../../../hooks/use-server-data';
import AstuteService from '../../services/astute-service/astute-service';
import { useParams } from 'react-router';
import { useAnalytics } from '../../../hooks/use-analytics';
import { DiscoverabilityPageLoad } from '../../../services/shortcode-service/shortcode-service';
import useSeoConfig from '../../../hooks/use-seo-config';
import BreadcrumbsAndViewBanner from '../breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import { CatSubcatFilteredResponse } from '../../models/cat-subcat-filtered-response';
import { enableLanguageLinksforTH } from '../../../components/utils/syndicate-util/syndicate-util';
import {
    getBbtErrorMessageForCategoryPage,
    getCurrentRootFromServerContext,
} from '../astute-topic/util/astute-topic-util';

interface CategoryPageParams {
    category: string;
}

const CategoryPageView = () => {
    const { category } = useParams<CategoryPageParams>();
    const bbtContent = useBbtContent();
    const homePagetitle = useAppInformationContent();
    const serverContext = useContext(ServerContext);
    const searchContent = useSearchContent();
    const currentRoot = getCurrentRootFromServerContext(serverContext);
    const domainRoot = `https://${serverContext.domain}${currentRoot}`;
    const categoriesResponse = useServerData('bbtCategories', async () => {
        try {
            const astuteService = new AstuteService();
            const {
                categories,
                filteredCategories,
            }: CatSubcatFilteredResponse = await astuteService.getAllFilteredCatAndSubcat(
                serverContext.currentLanguageRegionCode,
                (BRAND_ATTRIBUTES as any)[serverContext.brand].bbt,
                serverContext.brand,
                category,
                '',
                false,
                true
            );
            if (filteredCategories) {
                const hrefLangs = await astuteService.getHrefLangsForCategory(
                    filteredCategories.categoryId,
                    serverContext.brand
                );
                serverContext.hrefLangs = hrefLangs.join(' ');
                serverContext.metaDescription = filteredCategories.categoryName;
                serverContext.documentTitle = filteredCategories.categoryName;
            }
            return {
                filteredCategories: filteredCategories,
                categories: categories,
            };
        } catch (e) {
            return { error: `Category Page Error: ${e}` };
        }
    })[0];
    useSeoConfig('bbt-category', 'bbt-category', [
        {
            fieldName: 'category',
            fieldValue:
                categoriesResponse?.data?.filteredCategories?.categoryName ||
                '',
        },
    ]);

    const redirectionForCategoryView = async (errorMessage: string) => {
        if (errorMessage === SlackMessages.BBT_CONTENT_DISABLED) {
            window.location.href = `${currentRoot}/`;
        } else {
            window.location.href = `${currentRoot}#bbt`;
        }
    };
    const discoverabilityPageLoad: DiscoverabilityPageLoad = {
        category: category?.replaceAll('-', ' '),
    };
    const [fireEvents] = useAnalytics();
    useEffect(() => {
        enableLanguageLinksforTH();
        fireEvents(
            ['self-help-category'],
            undefined,
            {
                discoverabilityPageLoad: discoverabilityPageLoad,
            },
            false
        );
    }, []);
    useEffect(() => {
        const errorMessage = getBbtErrorMessageForCategoryPage(
            bbtContent,
            categoriesResponse
        );
        errorMessage && redirectionForCategoryView(errorMessage);
    }, []);
    const categoryBreadcrumbScript =
        bbtContent &&
        !bbtContent.hideBrowseByTopic &&
        `<script type="application/ld+json">{"@context": "https://schema.org","@type": "BreadcrumbList","itemListElement":[{"@type": "ListItem","position": 1,"item":{"@id": "${domainRoot}","name": "${homePagetitle?.applicationName}"}},{ "@type": "ListItem","position": 2,"item":{"@id": "${domainRoot}${categoriesResponse?.data?.filteredCategories?.categoryAbsoluteSeo}","name": "${categoriesResponse?.data?.filteredCategories?.categoryName}"}}]}</script>`;
    return searchContent &&
        bbtContent &&
        !bbtContent.hideBrowseByTopic &&
        categoriesResponse?.data?.filteredCategories ? (
        <div className="categorypage">
            <BreadcrumbsAndViewBanner
                currentPage={
                    categoriesResponse?.data?.filteredCategories
                        ?.categoryName || ''
                }
                hashId={'#bbt'}
                bbtTitle={bbtContent.title}
                bbtSubtitle={bbtContent.subTitle}
                isCatPage={true}
                searchClassName="sh-search"
            />

            <div className="browse-by-topic">
                <div
                    dangerouslySetInnerHTML={{
                        __html: categoryBreadcrumbScript || '',
                    }}
                />

                <BbtCategories
                    category={category}
                    categories={categoriesResponse?.data?.categories}
                    filteredCategories={
                        categoriesResponse?.data?.filteredCategories
                    }
                    bbtContent={bbtContent}
                />

                <div className="verticalline"></div>
                <section className="subcategories">
                    <div className="category-title">
                        <div className="mobile-title">
                            <a href="#bbt" className="title-link">
                                <FdsChevron
                                    type="unfilled"
                                    direction="left"
                                ></FdsChevron>

                                <h2 className="right-panel-title">
                                    {categoriesResponse?.data?.filteredCategories?.categoryName.toUpperCase()}
                                </h2>
                            </a>
                        </div>
                        <h2 className="desktop-title">
                            {categoriesResponse?.data?.filteredCategories?.categoryName.toUpperCase()}
                        </h2>
                    </div>
                    <ul className="right-subcategory">
                        {categoriesResponse?.data?.filteredCategories.subCategories.map(
                            subCateg => {
                                return (
                                    subCateg.allTopics &&
                                    subCateg.allTopics.length > 0 && (
                                        <li
                                            className="right-subcategory-list"
                                            key={subCateg.subCategoryId}
                                        >
                                            <SecondaryButton
                                                className="right-subcategory-link"
                                                link={`${currentRoot}${subCateg.subCategoryAbsoluteSeo}`}
                                            >
                                                <span className="subcategory-name">{`${subCateg.subCategoryName}`}</span>
                                                <span className="topics-length">{`(${subCateg.allTopics.length})`}</span>
                                            </SecondaryButton>
                                        </li>
                                    )
                                );
                            }
                        )}
                    </ul>
                </section>
            </div>
        </div>
    ) : (
        <></>
    );
};
export default CategoryPageView;
